import React, { useState, useEffect } from "react";
import QRCode from "qrcode";
import { ProgressSpinner } from "primereact/progressspinner";
import Popup from "reactjs-popup";
import { useHistory } from "react-router-dom";
import { api } from "../../../services/api";
import ym from "react-yandex-metrika";

// O backend cria a cobrança do PicPay com validade de 900s (PicpayService, LAMBDA-cash-store).
// A tela esconde o QR 1 minuto antes, para que um pagamento iniciado no fim do prazo ainda
// termine dentro da validade. Pix pago depois que a cobrança expira entra na conta da loja
// sem número do pedido e o item não é entregue.
const PICPAY_QR_VALIDITY_SECONDS = 14 * 60;

function formatRemaining(seconds) {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export function PixFlow({ transactionData, onRequirePersonalInfo }) {
  const [open, setOpen] = useState(true);
  const [brCode, setBrCode] = useState("");
  const [qrCodeImg, setQrCodeImg] = useState("");
  const [copied, setCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const history = useHistory();

  const isPicpayCharge =
    transactionData.gateway === "picpay-pix" || transactionData.gateway === "picpay";
  const isPix = transactionData.gateway !== "picpay";
  const expired = remainingSeconds === 0;

  const handleRedirect = () => {
    setOpen(false);
    history.push({
      pathname: '/',
      search: '',
    });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(brCode);
    setCopied(true);
  };

  async function startTransaction() {
    const body = {
      username: transactionData.username,
      server: transactionData.server,
      gateway: transactionData.gateway,
      currencyCode: transactionData.currencyCode,
      token: transactionData.token,
      utm_source: transactionData.utm_source,
      utm_medium: transactionData.utm_medium,
      utm_campaign: transactionData.utm_campaign,
      items: transactionData.items,
    }

    try {
      let response = await api.post("payment/purchase", body);

      if (response.data.status === "fail") {
        // Conta sem os dados pessoais obrigatórios (ex.: telefone): volta para o formulário.
        if (response.data.message === "missing-personal-info" && onRequirePersonalInfo) {
          onRequirePersonalInfo();
          return;
        }
        const msgs = {
          "invalid-email":    "E-mail inválido. Entre em contato com o suporte.",
          "invalid-cpf":      "CPF inválido. Entre em contato com o suporte.",
          "invalid-fullname": "Nome completo inválido. Entre em contato com o suporte.",
          "invalid-phone":    "Telefone inválido. Entre em contato com o suporte.",
          "payment-error":    "Erro ao processar o pagamento. Tente novamente ou entre em contato com o suporte.",
        };
        setErrorMessage(msgs[response.data.message] ?? "Erro ao processar o pagamento. Tente novamente ou entre em contato com o suporte.");
        return;
      }

      if (response.data.gatewayIsolado === true && response.data.status === "success") {
        ym("reachGoal", "purchase_request");
        setPurchaseSuccess(true);
        return;
      }

      ym("reachGoal", "purchase_request");

      let code = "";
      if (transactionData.gateway === "iugu-pix") {
        code = response.data.pix.qrcode_text;
      } else if (transactionData.gateway === "omie-pix") {
        code = response.data.cCopiaCola;
      } else if (
        transactionData.gateway === "picpay-pix" ||
        transactionData.gateway === "picpay"
      ) {
        code = response.data.redirect.qrCode;
      }

      if (code) {
        setBrCode(code);
        const dataUrl = await QRCode.toDataURL(code, { width: 256, margin: 2 });
        setQrCodeImg(dataUrl);
        if (isPicpayCharge) {
          setExpiresAt(Date.now() + PICPAY_QR_VALIDITY_SECONDS * 1000);
        }
      }
    } catch (error) {
      console.error("Error:", error);
      setErrorMessage("Erro ao processar o pagamento. Tente novamente ou entre em contato com o suporte.");
    }
  }

  useEffect(() => {
    startTransaction();
  }, []);

  // Conta a partir de um prazo absoluto, e não decrementando um contador: com a aba em segundo
  // plano o navegador atrasa o setInterval, e o contador ficaria para trás do prazo real.
  useEffect(() => {
    if (!expiresAt) return undefined;
    let intervalId;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setRemainingSeconds(remaining);
      if (remaining === 0) clearInterval(intervalId);
    };
    tick();
    intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [expiresAt]);

  return (
    <div className="spinner-div">
      {qrCodeImg !== "" ? (
        <>
          <Popup open={open} modal closeOnDocumentClick={false}>
            <div className="modal">
              <button className="close" onClick={handleRedirect}>
                ×
              </button>
              <img
                className="logo"
                src={"/img/raidhut.png"}
                alt="raidhut-logo"
              />
              <h1 className="modal-title">
                {transactionData.gateway === "picpay" ? "Pagamento via PicPay" : "Pagamento via Pix"}
              </h1>
              {expired ? (
                <>
                  <h3 className="modal-subtext">
                    Este QR Code expirou. Para concluir a compra, gere um novo pagamento.
                  </h3>
                  <button className="copy-button" onClick={handleRedirect}>
                    Voltar
                  </button>
                </>
              ) : (
                <>
                  {transactionData.gateway === "picpay" ? (
                    <>
                      <h3 className="modal-subtext">
                        Abra o aplicativo do{" "}
                        <img
                          src="/img/gateway/picpay-inline.png"
                          alt="PicPay"
                          style={{ height: "2.0em", verticalAlign: "bottom", margin: "0 0" }}
                        />{" "}
                        e escaneie o QrCode abaixo.
                      </h3>
                      <img src={qrCodeImg} alt="Qr Code" />
                    </>
                  ) : (
                    <>
                      <h3 className="modal-subtext">
                        Abra o aplicativo de banco em seu <br /> telefone e escaneie
                        o código abaixo:
                      </h3>
                      <img src={qrCodeImg} alt="Qr Code" />
                    </>
                  )}
                  {remainingSeconds !== null && (
                    <>
                      <p className="pix-timer">
                        Expira em <strong>{formatRemaining(remainingSeconds)}</strong>
                      </p>
                      <div
                        className="pix-timer-bar"
                        role="progressbar"
                        aria-label="Tempo restante para pagar"
                        aria-valuemin={0}
                        aria-valuemax={PICPAY_QR_VALIDITY_SECONDS}
                        aria-valuenow={remainingSeconds}
                      >
                        <div
                          className="pix-timer-bar-fill"
                          style={{ width: `${(remainingSeconds / PICPAY_QR_VALIDITY_SECONDS) * 100}%` }}
                        />
                      </div>
                    </>
                  )}
                  <button className="copy-button" onClick={handleCopy}>
                    {copied ? "Copiado!" : "Copiar"}
                  </button>
                  {isPix && (
                    <p className="pix-warning">
                      Pague somente com este QR Code ou copia e cola. Pix para a chave da
                      loja ou "Pix novamente" não são identificados e o item não é entregue.
                    </p>
                  )}
                </>
              )}
            </div>
          </Popup>
        </>
      ) : purchaseSuccess ? (
        <div className="success-message">
          <p>Compra realizada com sucesso! Em breve você receberá a confirmação do pagamento.</p>
          <button className="copy-button" onClick={handleRedirect}>Voltar</button>
        </div>
      ) : errorMessage ? (
        <div className="error-message">
          <p>{errorMessage}</p>
          <button className="copy-button" onClick={handleRedirect}>Voltar</button>
        </div>
      ) : (
        <ProgressSpinner />
      )}
    </div>
  );
}
